/**
 * `ExperimentsView` — ADR-0233 ops2026 "Live homepage experiment".
 *
 * Shows the live experiment on `https://sill.so/`: variant status,
 * weights, per-variant funnel (views → scan submitted → scan completed
 * → email submitted → signed up), per-step rates with 95% Wilson CIs,
 * p-values and probability-to-beat-control. A tiny signup-conversion
 * chart over time when the API supplies one.
 *
 * Role contract (ADR-0233 §UI; ADR-0218 STAFF_ROUTE_MIN_ROLE):
 *   - viewer:   can read every panel, but every mutation control is
 *               hidden (`canChange = false`).
 *   - admin+:   sees the write controls (weights editor, pause/resume,
 *               "Declare winner" with an in-page confirmation step,
 *               "Force a variant", "Start new experiment", preview links).
 *
 * API-missing (SPA deployed before the API): a 404 shows a calm
 * "not deployed yet" card, same convention as agent-registry /
 * trust-engine.
 */

import { ExternalLink, Pause, Play, RefreshCw, Trophy } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import type {
  AdminHomeExperimentPutBody,
  AdminHomeExperimentResponse,
  HomeExperimentFunnelRow,
  HomeExperimentWeights,
  HomeVariant,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useAuth } from '../../app/auth-context.js';
import { Card } from '../../components/atoms/Card.js';
import { ConfirmDialog } from '../../components/atoms/ConfirmDialog.js';
import { Pill } from '../../components/atoms/Pill.js';
import { TableScroll } from '../../components/atoms/TableScroll.js';
import { formatCount, formatIsoCompact } from '../../lib/format.js';
import { hasRole } from '../../lib/roles.js';
import { useC } from '../../lib/theme.js';

import { ConversionChart } from './ConversionChart.js';
import { Tooltip } from './Tooltip.js';
import { WeightsEditor } from './WeightsEditor.js';
import {
  FUNNEL_STEPS,
  STEP_LABEL,
  STATUS_LABEL,
  VARIANT_LABEL,
  VARIANT_SHORT,
  VARIANTS,
  conversionRate,
  findRow,
  formatPValue,
  formatPosterior,
  formatRatePct,
  isClearWinner,
  wilson95,
} from './experiments-helpers.js';

/** Tooltip copy for the Experiments view (ADR-0233). Each ≤ 20 words. */
const TIP = {
  status:
    'Running: new visitors split by weights. Paused: everyone gets control. Winner declared: everyone gets the winner.',
  startedAt: 'When the current experiment id first started collecting events.',
  split: 'Share of new visitors sent to each variant. Existing visitors keep their variant.',
  experimentId: 'Unique id for this run. Changes when you start a new experiment.',
  step: {
    view: 'Visitors who loaded the homepage. Bots, preview visits, and privacy-signal visitors are excluded.',
    cta_click:
      'Visitors who tapped a primary call-to-action. Bots, preview visits, and privacy-signal visitors excluded.',
    scan_submit:
      'Visitors who submitted a domain to scan. Bots, preview visits, and privacy-signal visitors excluded.',
    scan_complete:
      'Visitors whose scan finished. Bots, preview visits, and privacy-signal visitors excluded.',
    email_submit:
      'Visitors who entered an email. Bots, preview visits, and privacy-signal visitors excluded.',
    signup_created:
      'Visitors who created an account. Bots, preview visits, and privacy-signal visitors excluded.',
  },
  stepRate:
    'Conversion rate at this step over views, with its 95% confidence interval.',
  signupsViews: 'Count of signups over count of views for this variant.',
  signupRateCi:
    'Signup rate with a 95% confidence interval — the range the true rate likely sits in.',
  posterior:
    "Probability this variant's true signup rate is higher than the control's.",
  pValue:
    "Chance of seeing this gap vs the control if there's really no difference.",
  pauseRunning: 'Pause: everyone gets control. No new assignment cookies are set.',
  pausePaused: 'Resume: new visitors get assigned by weights again.',
  force:
    'Force a variant for new and returning humans. Bots still get control.',
  declare:
    'Declare winner: everyone, including bots, gets this variant. Experiment ends.',
  startNew: 'Mint a new experiment id. Every visitor is reassigned by weights.',
  controlVariant:
    'What bots, AI agents, privacy-signal visitors and fallbacks always see.',
  thresholds:
    'Thresholds for the Winner badge. Server echoes the live values back here.',
  thPosterior: 'Minimum probability-to-beat-control before the Winner badge appears.',
  thPValue: 'Maximum p-value vs control before the Winner badge appears.',
  thSignups: 'Minimum signups each variant needs before the Winner badge can appear.',
  previewLinks:
    '?hv= shows a variant for one visit. No cookie set, not tracked.',
  propagation: 'Changes reach visitors within 60 seconds.',
} as const;

/** Winner-rule tooltip, formatted with the live thresholds. */
function winnerRuleTip(th: {
  readonly posterior: number;
  readonly p_value: number;
  readonly min_signups_per_arm: number;
}): string {
  const post = Math.round(th.posterior * 100);
  const p = th.p_value.toFixed(2);
  return `Posterior ≥ ${post}% AND p ≤ ${p} AND ≥ ${th.min_signups_per_arm} signups per variant.`;
}

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

type ActionKind = 'declare_winner' | 'start_new' | 'resume';

interface ConfirmAction {
  readonly kind: ActionKind;
  readonly variant?: HomeVariant;
}

export function ExperimentsView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const auth = useAuth();
  const role = auth.state.kind === 'authenticated' ? auth.state.staff.role : undefined;
  const canChange = hasRole(role, 'admin');

  const [data, setData] = useState<AdminHomeExperimentResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [pending, setPending] = useState(false);

  const [draftWeights, setDraftWeights] = useState<HomeExperimentWeights | null>(null);
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const next = await api.getHomeExperiment();
      setData(next);
      setDraftWeights(next.config.weights);
      setMissing(false);
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'not_found') {
        setMissing(true);
      } else {
        setError(err instanceof ApiError ? err.message : 'Failed to load the experiment panel.');
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const apply = useCallback(
    async (body: AdminHomeExperimentPutBody): Promise<void> => {
      setPending(true);
      setError(null);
      try {
        const next = await api.putHomeExperiment(body);
        setData(next);
        setDraftWeights(next.config.weights);
        setConfirm(null);
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'forbidden') {
          setError('Admin role required to change the experiment.');
        } else {
          setError(err instanceof ApiError ? err.message : 'Change failed — retry.');
        }
      } finally {
        setPending(false);
      }
    },
    [api],
  );

  const config = data?.config ?? null;
  const funnel = data?.funnel ?? [];
  const control: HomeVariant = config?.control_variant ?? 'a';
  const controlRow = findRow(funnel, control);
  const thresholds = config?.thresholds;
  const timeSeries = data?.time_series ?? null;

  const dirtyWeights = useMemo((): boolean => {
    if (config === null || draftWeights === null) return false;
    return (
      draftWeights.a !== config.weights.a
      || draftWeights.b !== config.weights.b
      || draftWeights.c !== config.weights.c
    );
  }, [config, draftWeights]);

  const previewLinks = useMemo(
    () => VARIANTS.map((v) => ({ v, href: `https://sill.so/?hv=${v}` })),
    [],
  );

  const copyPreview = async (href: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(href);
    } catch {
      // Nothing to do — browsers may deny clipboard on some setups.
    }
  };

  const kicker: CSSProperties = {
    fontFamily: monoFont,
    fontSize: 10,
    letterSpacing: 1.5,
    color: C.muted,
    textTransform: 'uppercase',
    marginBottom: 8,
  };

  const section = (title: string): JSX.Element => (
    <div style={{ ...kicker, marginTop: 24 }}>{title}</div>
  );

  const renderHeader = (): JSX.Element => {
    const statusTone =
      data === null
        ? 'neutral'
        : data.status === 'running'
          ? 'lime'
          : data.status === 'paused'
            ? 'amber'
            : 'mint';
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 18,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={kicker}>ADR-0233 · Live homepage experiment</div>
          <h1 style={{ fontFamily: serifFont, fontSize: 28, color: C.text, margin: 0 }}>Experiments</h1>
          {data !== null && config !== null ? (
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.muted,
                marginTop: 4,
                display: 'inline-flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 4,
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Pill tone={statusTone}>{STATUS_LABEL[data.status]}</Pill>
                <Tooltip label={TIP.status} testId="tt-status" />
              </span>
              <span>Started</span>
              <span style={{ fontFamily: monoFont, color: C.body }}>
                {formatIsoCompact(config.started_at)}
              </span>
              <Tooltip label={TIP.startedAt} testId="tt-started" />
              <span>·</span>
              <span>Split</span>
              <span style={{ fontFamily: monoFont, color: C.body }}>
                A {config.weights.a}% / B {config.weights.b}% / C {config.weights.c}%
              </span>
              <Tooltip label={TIP.split} testId="tt-split" />
              <span>·</span>
              <span>Experiment</span>
              <span style={{ fontFamily: monoFont, color: C.body }}>{config.experiment_id}</span>
              <Tooltip label={TIP.experimentId} testId="tt-experiment-id" />
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void load()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: sansFont,
            fontSize: 12,
            padding: '6px 12px',
            background: 'transparent',
            color: C.muted,
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
    );
  };

  const renderNotEnoughData = (why: string): JSX.Element => (
    <div
      data-testid="not-enough-data"
      style={{
        fontFamily: sansFont,
        fontSize: 12.5,
        color: C.muted,
        marginTop: 6,
      }}
    >
      {why}
    </div>
  );

  const renderFunnel = (): JSX.Element | null => {
    if (data === null || config === null) return null;
    const totalViews = funnel.reduce((a, r) => a + r.view, 0);
    return (
      <>
        {section('Funnel per variant')}
        <Card style={{ padding: 16 }}>
          {totalViews === 0
            ? renderNotEnoughData('No traffic recorded yet for this experiment.')
            : null}
          <TableScroll minWidth={820}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontFamily: sansFont,
                fontSize: 12.5,
              }}
              data-testid="funnel-table"
            >
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: 'left',
                      fontFamily: monoFont,
                      fontSize: 10,
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      color: C.muted,
                      padding: '6px 10px',
                      borderBottom: `1px solid ${C.border}`,
                    }}
                  >
                    Step
                  </th>
                  {VARIANTS.map((v) => (
                    <th
                      key={v}
                      style={{
                        textAlign: 'right',
                        fontFamily: monoFont,
                        fontSize: 10,
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                        color: C.muted,
                        padding: '6px 10px',
                        borderBottom: `1px solid ${C.border}`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {VARIANT_SHORT[v]}
                      {v === control ? (
                        <span
                          style={{
                            fontFamily: monoFont,
                            fontSize: 9,
                            marginLeft: 4,
                            color: C.dim,
                            display: 'inline-flex',
                            alignItems: 'center',
                          }}
                        >
                          (control)
                          <Tooltip
                            label={TIP.controlVariant}
                            size={10}
                            testId={`tt-control-head-${v}`}
                            align="end"
                          />
                        </span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FUNNEL_STEPS.map((step) => (
                  <tr key={step}>
                    <td
                      style={{
                        padding: '6px 10px',
                        color: C.body,
                        borderBottom: `1px solid ${C.borderSoft}`,
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        {STEP_LABEL[step]}
                        <Tooltip label={TIP.step[step]} testId={`tt-step-${step}`} />
                      </span>
                    </td>
                    {VARIANTS.map((v) => {
                      const row = findRow(funnel, v);
                      if (row === null) {
                        return (
                          <td
                            key={v}
                            style={{
                              padding: '6px 10px',
                              textAlign: 'right',
                              borderBottom: `1px solid ${C.borderSoft}`,
                              color: C.muted,
                            }}
                          >
                            —
                          </td>
                        );
                      }
                      const rate = conversionRate(row, step);
                      const [lo, hi] = step === 'view' ? [0, 0] : wilson95(row[step], row.view);
                      return (
                        <td
                          key={v}
                          style={{
                            padding: '6px 10px',
                            textAlign: 'right',
                            borderBottom: `1px solid ${C.borderSoft}`,
                            color: C.text,
                            fontFamily: monoFont,
                            fontSize: 11.5,
                          }}
                        >
                          <div>{formatCount(row[step])}</div>
                          {step === 'view' ? null : (
                            <div style={{ color: C.muted, fontSize: 10.5 }}>
                              {formatRatePct(rate)} · 95%{' '}
                              <span style={{ whiteSpace: 'nowrap' }}>
                                [{formatRatePct(lo)}–{formatRatePct(hi)}]
                              </span>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
          {thresholds !== undefined ? (
            <div style={{ marginTop: 12, fontFamily: sansFont, fontSize: 12, color: C.muted }}>
              Winner rule — posterior ≥ {Math.round(thresholds.posterior * 100)}% AND p ≤{' '}
              {thresholds.p_value.toFixed(2)} AND signups ≥ {thresholds.min_signups_per_arm} per arm.
            </div>
          ) : null}
        </Card>

        {section('Signup rate vs control')}
        <Card style={{ padding: 16 }}>
          <TableScroll minWidth={760}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontFamily: sansFont,
                fontSize: 12.5,
              }}
              data-testid="stats-table"
            >
              <thead>
                <tr>
                  {(
                    [
                      { label: 'Variant', tip: null },
                      { label: 'Signups / Views', tip: TIP.signupsViews, testId: 'tt-col-signups-views' },
                      { label: 'Signup rate (95% CI)', tip: TIP.signupRateCi, testId: 'tt-col-rate-ci' },
                      { label: 'P(beats control)', tip: TIP.posterior, testId: 'tt-col-posterior' },
                      { label: 'p-value', tip: TIP.pValue, testId: 'tt-col-p-value' },
                      { label: '', tip: null },
                    ] as const
                  ).map((h, idx) => (
                    <th
                      key={`${h.label}-${idx}`}
                      style={{
                        textAlign: h.label === 'Variant' ? 'left' : 'right',
                        fontFamily: monoFont,
                        fontSize: 10,
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                        color: C.muted,
                        padding: '6px 10px',
                        borderBottom: `1px solid ${C.border}`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: h.label === 'Variant' ? 'flex-start' : 'flex-end', width: '100%' }}>
                        {h.label}
                        {h.tip !== null ? (
                          <Tooltip label={h.tip} testId={h.testId} align="end" />
                        ) : null}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {VARIANTS.map((v) => {
                  const row = findRow(funnel, v);
                  if (row === null) return null;
                  return (
                    <StatsRow
                      key={v}
                      row={row}
                      isControl={v === control}
                      controlRow={controlRow}
                      thresholds={thresholds}
                    />
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
          {thresholds !== undefined ? renderNotEnoughRowsNote(funnel, thresholds, control, C) : null}
        </Card>
      </>
    );
  };

  const renderChart = (): JSX.Element | null => {
    if (timeSeries === null || timeSeries === undefined || timeSeries.length === 0) return null;
    return (
      <>
        {section('Signup conversion over time')}
        <Card style={{ padding: 16 }}>
          <ConversionChart points={timeSeries} controlVariant={control} />
        </Card>
      </>
    );
  };

  const renderControls = (): JSX.Element | null => {
    if (!canChange || data === null || config === null || draftWeights === null) return null;
    return (
      <>
        {section('Controls')}
        <Card style={{ padding: 16, display: 'grid', gap: 20 }}>
          <div
            data-testid="propagation-note"
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              marginTop: -4,
            }}
          >
            {TIP.propagation}
          </div>
          <WeightsEditor
            value={draftWeights}
            onChange={setDraftWeights}
            onSave={() => void apply({ weights: draftWeights })}
            dirty={dirtyWeights}
            pending={pending}
          />

          <div style={{ borderTop: `1px solid ${C.borderSoft}` }} />

          <div
            style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() =>
                  config.paused
                    ? setConfirm({ kind: 'resume' })
                    : void apply({ paused: true })
                }
                disabled={pending || data.status === 'winner_declared'}
                data-testid="toggle-pause"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontFamily: sansFont,
                  fontSize: 12.5,
                  fontWeight: 600,
                  padding: '7px 12px',
                  background: 'transparent',
                  color: C.body,
                  border: `1px solid ${C.border}`,
                  borderRadius: 3,
                  cursor: pending ? 'not-allowed' : 'pointer',
                  opacity: pending ? 0.6 : 1,
                }}
              >
                {config.paused ? <Play size={12} /> : <Pause size={12} />}
                {config.paused ? 'Resume' : 'Pause'}
              </button>
              <Tooltip
                label={config.paused ? TIP.pausePaused : TIP.pauseRunning}
                testId="tt-pause"
              />
            </span>

            <ForcedVariantPicker
              value={config.forced_variant}
              disabled={pending}
              onChange={(next) => void apply({ forced_variant: next })}
            />

            <div style={{ flex: 1 }} />

            {data.status === 'winner_declared' ? (
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => setConfirm({ kind: 'start_new' })}
                  disabled={pending}
                  data-testid="start-new"
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12.5,
                    fontWeight: 600,
                    padding: '7px 12px',
                    background: 'transparent',
                    color: C.body,
                    border: `1px solid ${C.border}`,
                    borderRadius: 3,
                    cursor: pending ? 'not-allowed' : 'pointer',
                    opacity: pending ? 0.6 : 1,
                  }}
                >
                  Start new experiment
                </button>
                <Tooltip label={TIP.startNew} testId="tt-start-new" align="end" />
              </span>
            ) : null}
          </div>

          <DeclareWinnerPanel
            disabled={pending || data.status === 'winner_declared'}
            onDeclare={(variant) => setConfirm({ kind: 'declare_winner', variant })}
            winner={config.winner_variant}
          />

          <div style={{ borderTop: `1px solid ${C.borderSoft}` }} />

          <div>
            <ControlVariantPicker
              value={control}
              disabled={pending}
              onSave={(next) =>
                void apply({ control_variant: next } as unknown as AdminHomeExperimentPutBody)
              }
            />
          </div>

          <div style={{ borderTop: `1px solid ${C.borderSoft}` }} />

          <div>
            <div style={{ ...kicker, display: 'inline-flex', alignItems: 'center' }}>
              Thresholds
              <Tooltip label={TIP.thresholds} testId="tt-thresholds" />
            </div>
            <ThresholdsEditor
              value={config.thresholds}
              disabled={pending}
              onSave={(next) => void apply({ thresholds: next })}
            />
          </div>

          <div style={{ borderTop: `1px solid ${C.borderSoft}` }} />

          <div>
            <div style={{ ...kicker, display: 'inline-flex', alignItems: 'center' }}>
              Preview links (staff)
              <Tooltip label={TIP.previewLinks} testId="tt-preview-links" />
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              {previewLinks.map(({ v, href }) => (
                <div
                  key={v}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                >
                  <span style={{ fontFamily: sansFont, fontSize: 12.5, color: C.body }}>
                    {VARIANT_LABEL[v]}
                  </span>
                  <code
                    style={{
                      fontFamily: monoFont,
                      fontSize: 11,
                      background: C.surface2,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      padding: '2px 6px',
                      color: C.body,
                    }}
                  >
                    {href}
                  </code>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontFamily: sansFont,
                      fontSize: 12,
                      color: C.muted,
                      textDecoration: 'none',
                    }}
                  >
                    <ExternalLink size={11} /> Open
                  </a>
                  <button
                    type="button"
                    onClick={() => void copyPreview(href)}
                    style={{
                      fontFamily: sansFont,
                      fontSize: 12,
                      color: C.muted,
                      background: 'transparent',
                      border: `1px solid ${C.border}`,
                      padding: '2px 8px',
                      borderRadius: 2,
                      cursor: 'pointer',
                    }}
                  >
                    Copy
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </>
    );
  };

  const renderAudit = (): JSX.Element | null => {
    if (data === null || data.audit.length === 0) return null;
    return (
      <>
        {section('Change log')}
        <Card style={{ padding: 16 }}>
          <TableScroll minWidth={520}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={auditTh(C)}>When</th>
                  <th style={auditTh(C)}>Change</th>
                  <th style={{ ...auditTh(C), textAlign: 'right' }}>By</th>
                </tr>
              </thead>
              <tbody data-testid="audit-table">
                {data.audit.map((entry, i) => (
                  <tr key={`${entry.at}-${i}`}>
                    <td style={auditTd(C, true)}>{formatIsoCompact(entry.at)}</td>
                    <td style={auditTd(C, false)}>{entry.change}</td>
                    <td
                      style={{
                        ...auditTd(C, false),
                        textAlign: 'right',
                        fontFamily: sansFont,
                      }}
                    >
                      {entry.staff_name ?? entry.staff_id ?? 'system'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        </Card>
      </>
    );
  };

  const confirmDialog = ((): ReactNode => {
    if (confirm === null) return null;
    if (confirm.kind === 'declare_winner' && confirm.variant !== undefined) {
      const v = confirm.variant;
      return (
        <ConfirmDialog
          open
          tone="coral"
          title={`Declare ${VARIANT_LABEL[v]} the winner?`}
          body={
            <>
              100% of real traffic to <code>https://sill.so/</code> will serve variant{' '}
              <strong>{VARIANT_SHORT[v]}</strong>. The current experiment ends and this choice is
              recorded in the change log. You can start a new experiment afterward.
            </>
          }
          confirmLabel={`Declare ${VARIANT_SHORT[v]} the winner`}
          pending={pending}
          onConfirm={() => void apply({ winner_variant: v })}
          onCancel={() => setConfirm(null)}
        />
      );
    }
    if (confirm.kind === 'start_new') {
      return (
        <ConfirmDialog
          open
          tone="coral"
          title="Start a new experiment?"
          body={
            <>
              A new <code>experiment_id</code> is minted, the winner is cleared, and the three
              variants start collecting fresh events against the current weights. Historical
              counts stay in the staff log and in the events archive.
            </>
          }
          confirmLabel="Start new experiment"
          pending={pending}
          onConfirm={() => void apply({ start_new: true })}
          onCancel={() => setConfirm(null)}
        />
      );
    }
    if (confirm.kind === 'resume') {
      return (
        <ConfirmDialog
          open
          tone="lime"
          title="Resume the experiment?"
          body="Real traffic will flow across the configured weights again."
          confirmLabel="Resume"
          pending={pending}
          onConfirm={() => void apply({ paused: false })}
          onCancel={() => setConfirm(null)}
        />
      );
    }
    return null;
  })();

  return (
    <div style={{ padding: 24, maxWidth: 1040 }}>
      {renderHeader()}

      {missing ? (
        <Card style={{ padding: 16 }}>
          <div style={{ fontFamily: sansFont, fontSize: 13.5, color: C.body }}>
            The home-experiment API is not deployed yet. This page will work once the API
            release that adds it is live; nothing is broken in the meantime.
          </div>
        </Card>
      ) : null}

      {error !== null ? (
        <Card style={{ padding: 16, marginBottom: 12 }}>
          <div role="alert" style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>
            {error}
          </div>
        </Card>
      ) : null}

      {loading && data === null ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>Loading…</div>
      ) : null}

      {!canChange && data !== null ? (
        <Card style={{ padding: 12, marginBottom: 12 }}>
          <div
            data-testid="viewer-notice"
            style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted }}
          >
            Read-only. Admins can change the weights, pause the experiment, declare a winner,
            or force a variant.
          </div>
        </Card>
      ) : null}

      {data !== null ? (
        <>
          {renderFunnel()}
          {renderChart()}
          {renderControls()}
          {renderAudit()}
        </>
      ) : null}

      {confirmDialog}
    </div>
  );
}

function auditTh(C: ReturnType<typeof useC>): CSSProperties {
  return {
    textAlign: 'left',
    fontFamily: monoFont,
    fontSize: 10,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    color: C.muted,
    padding: '0 0 10px',
  };
}

function auditTd(C: ReturnType<typeof useC>, mono: boolean): CSSProperties {
  return {
    padding: '8px 16px 8px 0',
    color: mono ? C.muted : C.body,
    fontFamily: mono ? monoFont : sansFont,
    fontSize: mono ? 11 : 13,
    borderBottom: `1px solid ${C.borderSoft}`,
    whiteSpace: mono ? 'nowrap' : 'normal',
    verticalAlign: 'top',
  };
}

function StatsRow({
  row,
  isControl,
  controlRow,
  thresholds,
}: {
  row: HomeExperimentFunnelRow;
  isControl: boolean;
  controlRow: HomeExperimentFunnelRow | null;
  thresholds: AdminHomeExperimentResponse['config']['thresholds'] | undefined;
}): JSX.Element {
  const C = useC();
  const [lo, hi] = wilson95(row.signup_created, row.view);
  const stats = row.stats;
  const serverSaysWinner = stats?.is_clear_winner === true && !isControl;
  const localSaysWinner =
    !isControl
    && stats !== undefined
    && thresholds !== undefined
    && controlRow !== null
    && isClearWinner({
      variant: row.variant,
      posterior: stats.posterior_beats_control,
      p_value: stats.p_value_vs_control,
      variant_signups: row.signup_created,
      control_signups: controlRow.signup_created,
      variant_conversion: row.view > 0 ? row.signup_created / row.view : 0,
      control_conversion: controlRow.view > 0 ? controlRow.signup_created / controlRow.view : 0,
      thresholds,
    });
  const winner = serverSaysWinner || localSaysWinner;

  const td = {
    padding: '6px 10px',
    borderBottom: `1px solid ${C.borderSoft}`,
    color: C.text,
    fontFamily: monoFont,
    fontSize: 11.5,
  } as const;
  const tdLeft = { ...td, fontFamily: sansFont, fontSize: 13, color: C.body, textAlign: 'left' as const };
  const tdRight = { ...td, textAlign: 'right' as const };
  return (
    <tr data-testid={`stats-row-${row.variant}`}>
      <td style={tdLeft}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span>{VARIANT_LABEL[row.variant]}</span>
          {isControl ? <Pill tone="neutral">Control</Pill> : null}
          {winner ? (
            <span
              data-testid={`winner-badge-${row.variant}`}
              style={{ display: 'inline-flex', alignItems: 'center' }}
            >
              <Pill tone="lime">
                <Trophy size={10} /> Winner
              </Pill>
              <Tooltip
                label={
                  thresholds !== undefined
                    ? winnerRuleTip(thresholds)
                    : 'Posterior AND p-value AND minimum signups all met.'
                }
                testId={`tt-winner-${row.variant}`}
              />
            </span>
          ) : null}
        </div>
      </td>
      <td style={tdRight}>
        {formatCount(row.signup_created)} / {formatCount(row.view)}
      </td>
      <td style={tdRight}>
        {formatRatePct(stats?.signup_rate ?? (row.view > 0 ? row.signup_created / row.view : 0))}{' '}
        <span style={{ color: C.muted }}>
          [{formatRatePct(stats?.signup_rate_ci_low ?? lo)}–{formatRatePct(stats?.signup_rate_ci_high ?? hi)}]
        </span>
      </td>
      <td style={tdRight}>{isControl ? '—' : formatPosterior(stats?.posterior_beats_control)}</td>
      <td style={tdRight}>{isControl ? '—' : formatPValue(stats?.p_value_vs_control)}</td>
      <td style={tdRight} />
    </tr>
  );
}

function renderNotEnoughRowsNote(
  funnel: readonly HomeExperimentFunnelRow[],
  thresholds: { readonly min_signups_per_arm: number },
  control: HomeVariant,
  C: ReturnType<typeof useC>,
): ReactNode {
  const controlRow = findRow(funnel, control);
  if (controlRow === null) return null;
  const anyBelow = funnel.some((r) => r.signup_created < thresholds.min_signups_per_arm);
  if (!anyBelow) return null;
  return (
    <div
      data-testid="not-enough-signups"
      style={{
        marginTop: 10,
        fontFamily: sansFont,
        fontSize: 12,
        color: C.muted,
      }}
    >
      Not enough data yet to call a winner — at least {thresholds.min_signups_per_arm} signups are
      needed on each arm.
    </div>
  );
}

function ForcedVariantPicker({
  value,
  disabled,
  onChange,
}: {
  value: HomeVariant | null;
  disabled: boolean;
  onChange: (next: HomeVariant | null) => void;
}): JSX.Element {
  const C = useC();
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontFamily: "'Geist', sans-serif",
          fontSize: 12.5,
          color: C.body,
        }}
      >
        Force variant:
        <select
          value={value ?? ''}
          disabled={disabled}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === '' ? null : (v as HomeVariant));
          }}
          aria-label="Force a variant for new and returning humans"
          data-testid="forced-variant"
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            padding: '4px 8px',
            background: C.surface2,
            color: C.text,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
          }}
        >
          <option value="">(none — use weights)</option>
          {VARIANTS.map((v) => (
            <option key={v} value={v}>
              {VARIANT_LABEL[v]}
            </option>
          ))}
        </select>
      </label>
      <Tooltip label={TIP.force} testId="tt-force" />
    </span>
  );
}

/**
 * `ControlVariantPicker` — admin-only control that assigns which arm is
 * the "control" (what bots, privacy-signal visitors, and the fallback
 * cohort always see). PUTs `control_variant`. A sibling agent is
 * confirming the API side; this UI codes to the field name
 * `control_variant` on `AdminHomeExperimentPutBody` and casts because
 * the TS type does not yet declare it. When the server type lands, the
 * cast collapses with no code change here.
 */
function ControlVariantPicker({
  value,
  disabled,
  onSave,
}: {
  value: HomeVariant;
  disabled: boolean;
  onSave: (next: HomeVariant) => void;
}): JSX.Element {
  const C = useC();
  const [pick, setPick] = useState<HomeVariant>(value);
  const dirty = pick !== value;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 1.5,
          color: C.muted,
          textTransform: 'uppercase',
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        Control variant
        <Tooltip label={TIP.controlVariant} testId="tt-control-variant" />
      </div>
      <select
        value={pick}
        disabled={disabled}
        onChange={(e) => setPick(e.target.value as HomeVariant)}
        aria-label="Control variant"
        data-testid="control-variant"
        style={{
          fontFamily: monoFont,
          fontSize: 12,
          padding: '4px 8px',
          background: C.surface2,
          color: C.text,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
        }}
      >
        {VARIANTS.map((v) => (
          <option key={v} value={v}>
            {VARIANT_LABEL[v]}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={disabled || !dirty}
        onClick={() => onSave(pick)}
        data-testid="save-control-variant"
        style={{
          fontFamily: "'Geist', sans-serif",
          fontSize: 12.5,
          fontWeight: 600,
          padding: '7px 12px',
          background: dirty ? C.lime : 'transparent',
          color: dirty ? C.ink : C.muted,
          border: `1px solid ${dirty ? C.lime : C.border}`,
          borderRadius: 3,
          cursor: dirty && !disabled ? 'pointer' : 'not-allowed',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        Save control
      </button>
    </div>
  );
}

function DeclareWinnerPanel({
  disabled,
  onDeclare,
  winner,
}: {
  disabled: boolean;
  onDeclare: (v: HomeVariant) => void;
  winner: HomeVariant | null;
}): JSX.Element {
  const C = useC();
  const [pick, setPick] = useState<HomeVariant | ''>('');
  if (winner !== null) {
    return (
      <div
        data-testid="winner-set"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontFamily: "'Geist', sans-serif",
          fontSize: 12.5,
          color: C.body,
        }}
      >
        <Pill tone="mint">
          <Trophy size={10} /> Winner: {VARIANT_SHORT[winner]}
        </Pill>
        <span style={{ color: C.muted }}>100% of traffic serves {VARIANT_LABEL[winner]}.</span>
        <Tooltip label={TIP.declare} testId="tt-winner-set" />
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <label
        style={{
          fontFamily: "'Geist', sans-serif",
          fontSize: 12.5,
          color: C.body,
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        Declare winner:
        <Tooltip label={TIP.declare} testId="tt-declare" />
      </label>
      <select
        value={pick}
        disabled={disabled}
        aria-label="Pick a variant to declare the winner"
        data-testid="declare-pick"
        onChange={(e) => setPick(e.target.value as HomeVariant | '')}
        style={{
          fontFamily: monoFont,
          fontSize: 12,
          padding: '4px 8px',
          background: C.surface2,
          color: C.text,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
        }}
      >
        <option value="">pick a variant…</option>
        {VARIANTS.map((v) => (
          <option key={v} value={v}>
            {VARIANT_LABEL[v]}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={disabled || pick === ''}
        data-testid="declare-winner"
        onClick={() => pick !== '' && onDeclare(pick)}
        style={{
          fontFamily: "'Geist', sans-serif",
          fontSize: 12.5,
          fontWeight: 600,
          padding: '7px 12px',
          background: pick === '' ? 'transparent' : C.coralFg,
          color: pick === '' ? C.muted : C.text,
          border: `1px solid ${pick === '' ? C.border : C.coralFg}`,
          borderRadius: 3,
          cursor: pick === '' ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        Declare…
      </button>
    </div>
  );
}

function ThresholdsEditor({
  value,
  disabled,
  onSave,
}: {
  value: { readonly posterior: number; readonly p_value: number; readonly min_signups_per_arm: number };
  disabled: boolean;
  onSave: (next: {
    readonly posterior: number;
    readonly p_value: number;
    readonly min_signups_per_arm: number;
  }) => void;
}): JSX.Element {
  const C = useC();
  const [posterior, setPosterior] = useState(value.posterior);
  const [pValue, setPValue] = useState(value.p_value);
  const [minSignups, setMinSignups] = useState(value.min_signups_per_arm);
  const dirty =
    posterior !== value.posterior
    || pValue !== value.p_value
    || minSignups !== value.min_signups_per_arm;
  const invalid =
    !Number.isFinite(posterior)
    || posterior < 0.5
    || posterior > 0.9999
    || !Number.isFinite(pValue)
    || pValue <= 0
    || pValue > 0.5
    || !Number.isInteger(minSignups)
    || minSignups < 1;

  const field = (
    id: string,
    label: string,
    tip: string,
    tipTestId: string,
    val: number,
    onChange: (n: number) => void,
    step: number,
    min: number,
    max: number,
  ): JSX.Element => (
    <div style={{ display: 'grid', gap: 4 }}>
      <label
        htmlFor={id}
        style={{
          fontFamily: "'Geist', sans-serif",
          fontSize: 12,
          color: C.body,
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        {label}
        <Tooltip label={tip} testId={tipTestId} />
      </label>
      <input
        id={id}
        type="number"
        step={step}
        min={min}
        max={max}
        value={val}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          width: 110,
          fontFamily: monoFont,
          fontSize: 12,
          padding: '4px 8px',
          background: C.surface2,
          color: C.text,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
        }}
      />
    </div>
  );
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      {field('th-posterior', 'Posterior ≥', TIP.thPosterior, 'tt-th-posterior', posterior, setPosterior, 0.01, 0.5, 0.9999)}
      {field('th-p', 'p-value ≤', TIP.thPValue, 'tt-th-p', pValue, setPValue, 0.01, 0.0001, 0.5)}
      {field('th-signups', 'Signups per arm ≥', TIP.thSignups, 'tt-th-signups', minSignups, (n) => setMinSignups(Math.round(n)), 1, 1, 100000)}
      <button
        type="button"
        disabled={disabled || !dirty || invalid}
        onClick={() => onSave({ posterior, p_value: pValue, min_signups_per_arm: minSignups })}
        data-testid="save-thresholds"
        style={{
          fontFamily: "'Geist', sans-serif",
          fontSize: 12.5,
          fontWeight: 600,
          padding: '7px 12px',
          background: dirty && !invalid ? C.lime : 'transparent',
          color: dirty && !invalid ? C.ink : C.muted,
          border: `1px solid ${dirty && !invalid ? C.lime : C.border}`,
          borderRadius: 3,
          cursor: dirty && !invalid ? 'pointer' : 'not-allowed',
        }}
      >
        Save thresholds
      </button>
    </div>
  );
}
